import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { V, serif, mono, eyebrow, card, btnGold, btnGhost } from '../theme'


function TOSColumnsEditorMockup() {
  const cols = [
    { name: 'Last',          highlight: false },
    { name: 'Bid',           highlight: false },
    { name: 'Ask',           highlight: false },
    { name: 'Delta',         highlight: true  },
    { name: 'Prob. OTM',     highlight: true  },
    { name: '% Change',      highlight: false },
    { name: 'Open Interest', highlight: true  },
    { name: 'Bid Size',      highlight: false },
    { name: 'Ask Size',      highlight: false },
  ];
  return (
    <div style={{ marginBottom: '16px' }}>
      {/* Phone mockup container */}
      <div style={{ maxWidth: '320px', margin: '0 auto', background: '#000000', borderRadius: '12px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)' }}>

        {/* Header row */}
        <div style={{ background: '#000000', padding: '16px 20px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', borderBottom: '0.5px solid rgba(255,255,255,0.1)' }}>
          <div style={{ fontSize: '17px', color: '#ffffff', fontFamily: "'Jost',sans-serif", fontWeight: 700, lineHeight: 1.2 }}>← Columns Editor</div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '17px', color: '#007AFF', fontFamily: "'Jost',sans-serif", fontWeight: 700, lineHeight: 1.2 }}>✓</div>
            <div style={{ fontSize: '14px', color: '#007AFF', fontFamily: "'Jost',sans-serif", marginTop: '2px', lineHeight: 1.2 }}>Reset</div>
          </div>
        </div>

        {/* Column rows */}
        {cols.map((col, i) => (
          <div key={col.name} style={{
            background: '#000000',
            borderBottom: i < cols.length - 1 ? '0.5px solid rgba(255,255,255,0.1)' : 'none',
            borderLeft: col.highlight ? '3px solid #007AFF' : '3px solid transparent',
            padding: '14px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span style={{ fontSize: '16px', color: '#007AFF', lineHeight: 1, flexShrink: 0 }}>●</span>
              <span style={{ fontSize: '16px', color: '#ffffff', fontFamily: "'Jost',sans-serif", marginLeft: '16px', fontWeight: col.highlight ? 600 : 400 }}>{col.name}</span>
            </div>
            <span style={{ fontSize: '18px', color: 'rgba(255,255,255,0.6)', fontFamily: "'Jost',sans-serif", letterSpacing: '1px', lineHeight: 1 }}>≡</span>
          </div>
        ))}
      </div>

      {/* Legend note */}
      <div style={{ maxWidth: '320px', margin: '8px auto 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div style={{ width: '14px', height: '14px', borderLeft: '3px solid #007AFF', flexShrink: 0 }} />
        <span style={{ fontSize: '11px', color: 'rgba(201,168,76,0.7)', fontFamily: "'Jost',sans-serif", letterSpacing: '0.04em' }}>Highlighted = most important for premium sellers</span>
      </div>
    </div>
  );
}

function Ch0View({ state, onUpdate, onNavigate, onBack }) {
  const CHECKLIST_ITEMS = [
    'I have downloaded ThinkorSwim',
    'I have set up paper trading mode',
    'I have added the 20 day SMA in Blue',
    'I have added the 50 day SMA in Green',
    'I have added the 200 day SMA in Red',
    'I have added RSI below my main chart',
    'I know how to navigate to a stock\'s options chain on ThinkorSwim mobile',
    'I have set up my options chain view',
    'I have set Strikes to \'All Strikes\'',
    'I have set Exchange to \'Best\'',
    'I have set up my options chain columns using the Columns Editor',
    'I have set up my Positions page columns: Mark | P/L Day | P/L Open | Delta | % Change | P/L %',
    'I have explored the simulated TOS platform',
    'I understand what Open Interest means and how to find it on the options chain (covered in Chapter 4)',
  ];

  const checklist = (state && state.chapter0Checklist) || {};
  const completedCount = CHECKLIST_ITEMS.filter((_, i) => checklist[i]).length;
  const allComplete = completedCount === CHECKLIST_ITEMS.length;

  const toggleItem = (i) => {
    const newChecklist = { ...checklist, [i]: !checklist[i] };
    const newCount = CHECKLIST_ITEMS.filter((_, idx) => newChecklist[idx]).length;
    const patch = { chapter0Checklist: newChecklist };
    if (newCount === CHECKLIST_ITEMS.length) {
      const completed = (state && state.completedChapters) || [];
      if (!completed.includes('ch00')) patch.completedChapters = [...completed, 'ch00'];
    }
    onUpdate && onUpdate(patch);
  };

  const sections = [
    {
      step: 1, title: 'Download ThinkorSwim',
      body: `ThinkorSwim is available as a mobile app (iOS and Android) and as a desktop application (Mac and Windows). You do not need to fund a brokerage account to use it — a paper trading (simulated) account is available for free with no deposit required.

For this program, the desktop application is strongly recommended for learning. The larger screen gives you space to see your chart, your options chain, and your positions simultaneously — exactly the way the lessons are taught.

To download: visit the TD Ameritrade or Schwab website and search for ThinkorSwim download, or search "ThinkorSwim download" directly. The platform is free to use for paper trading indefinitely.`,
    },
    {
      step: 2, title: 'Setting Up Your Moving Averages',
      body: `For the strategies you are learning first — Cash Secured Puts, Covered Calls, and The Wheel — you need three moving averages on your daily chart.

These three lines will show you the trend, the equilibrium, and the health of any stock you are considering trading.`,
      cards: [
        {
          label: '20 Day SMA',
          color: '#7a9ec4',
          bg: 'rgba(122,158,196,0.1)',
          border: 'rgba(122,158,196,0.4)',
          desc: 'Short term trend. Your first line of support in an uptrend. When price pulls back to the 20 day MA in a healthy uptrend — that is often your entry zone.',
          tos: 'Period: 20  |  Type: SMA  |  Color: Blue',
        },
        {
          label: '50 Day SMA',
          color: '#7aab8a',
          bg: 'rgba(122,171,138,0.1)',
          border: 'rgba(122,171,138,0.4)',
          desc: 'Medium term trend. Shows the broader momentum of the stock. Price above the 50 day MA = healthy uptrend.',
          tos: 'Period: 50  |  Type: SMA  |  Color: Green',
        },
        {
          label: '200 Day SMA',
          color: '#c47a7a',
          bg: 'rgba(196,122,122,0.1)',
          border: 'rgba(196,122,122,0.4)',
          desc: 'The long term trend. The big picture. Only trade stocks above their 200 day MA when selling puts or covered calls. Below the 200 day MA is a long term downtrend — be very careful.',
          tos: 'Period: 200  |  Type: SMA  |  Color: Red',
        },
      ],
      cardNote: 'These three moving averages are your foundation setup. As you advance and explore shorter term trading strategies your mentor will guide you on additional indicator configurations. For now these three lines tell you everything you need to know about a stock\'s trend.',
    },
    {
      step: 3, title: 'RSI',
      body: `RSI stands for Relative Strength Index. It measures whether a stock is overbought or oversold on a scale of 0–100. Add it as a panel below your main chart.

Above 70 = overbought — potential pullback or exhaustion.
Below 30 = oversold — potential bounce.
Between 40–60 = neutral — no strong signal.

RSI helps you time your entries. If you are considering selling a put and RSI is already at 75 and declining, you may be entering into exhaustion. If RSI is bouncing from 30 while price holds above key support, that is a much better entry window. Do not use RSI in isolation — always confirm with price structure and MA position.`,
    },
    {
      step: 4, title: 'Your Options Chain',
      topCard: {
        icon: '📱',
        label: 'STEP 1 — GETTING TO THE OPTIONS CHAIN',
        body: `Before you can see the options chain for any stock you need to navigate to that stock's detail page first.\n\nHere is how:\n\n1. Open ThinkorSwim on your phone\n2. Tap the search icon and type the stock symbol you want to trade (e.g. AAPL, AMD, SPX)\n3. Tap the stock from the search results to open its detail page\n4. On the stock detail page scroll down or look for the 'Options' tab\n5. Tap 'Options' to open the options chain for that stock\n\nYou will now see the full options chain where you can select your expiration date, set your strikes to All Strikes, set Exchange to Best, and choose the strike you want to trade.`,
      },
      body: `Set up your options chain to show both Calls and Puts side by side with the Strike in the center. This is the layout used in the Options Chain Simulator in this program.

Set your default to show All Strikes and the expiration closest to 30–45 days out. Add these columns to your chain: Delta, Theta, IV (Implied Volatility), Bid, Ask, and Volume.

These six data points are everything you need to evaluate a trade:
· Delta tells you probability of success.
· Theta tells you your daily income.
· IV tells you if premium is rich or cheap.
· Bid/Ask tells you your fill range.
· Volume tells you if the option is liquid.`,
      linkText: 'Practice on the simulated options chain →',
      linkView: 'simulator',
    },
    {
      step: 5, title: 'Setting Up Your Positions Page',
      body: `Your positions page is your cockpit. Set it up once and it tells you everything you need to know at a glance.`,
      positionsColumns: [
        { name: 'Mark',      desc: 'Current market price of your position' },
        { name: 'P/L Day',   desc: 'How much you have made or lost today' },
        { name: 'P/L Open',  desc: 'How much you have made or lost since you opened the position' },
        { name: 'Delta',     desc: 'Your directional exposure — how much the position moves per $1 change in the underlying' },
        { name: '% Change',  desc: 'Percentage change in the position value today' },
        { name: 'P/L %',     desc: 'Your total return on this position as a percentage' },
      ],
      positionsNote: 'In ThinkorSwim right click any column header on your Positions page to add, remove, or reorder columns. Drag columns left or right to arrange them in this order.',
    },
    {
      step: 6, title: 'Setting Up Your Options Chain',
      body: `The options chain is where you will select your strikes and place your trades. Setting it up correctly means you can see all available options at a glance and find the best price.`,
      cards: [
        {
          label: 'STRIKES',
          color: V.gold,
          bg: V.bg3,
          border: 'rgba(201,168,76,0.2)',
          icon: '🎯',
          desc: `On your phone tap 'Strikes' at the top of the options chain and select 'All Strikes'.\n\nThis shows every available strike price so you can see the full picture and choose the exact strike that fits your strategy.`,
          tos: null,
        },
        {
          label: 'EXCHANGE',
          color: V.gold,
          bg: V.bg3,
          border: 'rgba(201,168,76,0.2)',
          icon: '💱',
          desc: `Tap 'Exchange' and select 'Best'.\n\nThis ensures you are always seeing the best available bid and ask prices across all exchanges — not just one. You want the best price when you enter and exit a trade.`,
          tos: null,
        },
        {
          label: 'CUSTOMIZING YOUR COLUMNS',
          color: V.gold,
          bg: V.bg3,
          border: 'rgba(201,168,76,0.2)',
          icon: '⚙️',
          desc: `Tap the gear icon ⚙️ on your options chain page to open the Columns Editor.\n\nThe eye icon on the left shows or hides each column. The lines on the right let you drag each row to reorder your columns.\n\nThe recommended column order for premium sellers is shown in the illustration below.`,
          tos: null,
        },
      ],
      showColumnsEditor: true,
      columnsEditorNote: 'In ThinkorSwim mobile tap the gear icon ⚙️ on your options chain to open the Columns Editor shown above.\n\nThe eye icon on the left shows or hides each column. The lines on the right let you drag each row up or down to reorder.\n\nSet up your columns in this order for the clearest view of the chain:\nLast · Bid · Ask · Delta · Prob. OTM · % Change · Open Interest\n\nTap the ✓ checkmark when done to save your layout.',
      cardNote: 'Take your time setting this up during your first mentoring session. We will walk through it together and make sure your chain is showing exactly what you need to see before you place your first paper trade.',
    },
  ];

  return (
    <div style={{ padding: '40px 32px', maxWidth: '760px', background: V.bg, minHeight: '100vh' }}>
      <button style={{ ...btnGhost, marginBottom: '28px', fontSize: '12px' }} onClick={onBack}>← Lessons</button>

      <div style={{ fontSize: '10px', letterSpacing: '0.15em', textTransform: 'uppercase', color: V.muted, fontFamily: "'Jost',sans-serif", marginBottom: '10px' }}>Chapter 0</div>
      <h1 style={{ ...serif, fontSize: '32px', fontWeight: 400, color: V.gold, margin: '0 0 20px' }}>Chapter 0 — Setting Up Your Platform</h1>

      {/* Mentoring callout */}
      <div style={{ background: 'rgba(201,168,76,0.08)', borderLeft: '3px solid #c9a84c', borderRadius: '8px', padding: '16px 20px', marginBottom: '28px', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
        <span style={{ fontSize: '18px', lineHeight: 1, flexShrink: 0, marginTop: '2px' }}>🎓</span>
        <p style={{ fontSize: '14px', color: 'rgba(240,237,230,0.85)', fontFamily: "'Jost',sans-serif", lineHeight: 1.8, margin: 0 }}>
          Setting up your platform is the foundation of everything that follows — and you will not do it alone. We will walk through this together in your first mentoring session. Use this checklist to familiarize yourself beforehand, and come to our first call with any questions. Your platform will be ready before you trade your first simulated dollar.
        </p>
      </div>

      {/* Opening quote */}
      <div style={{ background: V.bg2, borderLeft: `3px solid ${V.gold}`, borderRadius: '10px', padding: '18px 22px', marginBottom: '24px' }}>
        <p style={{ ...serif, fontSize: '17px', fontStyle: 'italic', color: V.gold, lineHeight: 1.6, margin: '0 0 6px' }}>
          "An investment in knowledge pays the best interest."
        </p>
        <div style={{ fontSize: '12px', color: V.muted, fontFamily: "'Jost',sans-serif" }}>— Benjamin Franklin</div>
      </div>

      {/* Introduction */}
      <div style={{ maxWidth: '680px', marginBottom: '24px' }}>
        <p style={{ fontSize: '14px', color: V.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.9, marginBottom: '14px' }}>
          Before you place your first trade — before you read your first chapter — you need one thing: a platform that shows you what the market is doing.
        </p>
        <p style={{ fontSize: '14px', color: V.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.9, marginBottom: '14px' }}>
          ThinkorSwim by TD Ameritrade is the platform this program is built around. It is free, powerful, and used by professional traders worldwide. Setting it up correctly from the beginning means you will see exactly what this program teaches you to look for — the same indicators, the same layout, the same information — every time you open it.
        </p>
        <p style={{ fontSize: '14px', color: V.text, fontFamily: "'Jost',sans-serif", lineHeight: 1.9, marginBottom: '6px', fontWeight: 500 }}>A note before you begin:</p>
        <p style={{ fontSize: '14px', color: V.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.9, marginBottom: '14px' }}>
          ThinkorSwim will show you every trade you have ever made. It will show you your P&L, your Greeks, your position history, your charts. What it cannot show you is why you made those decisions — what you were feeling when you entered, what part of you overrode your plan, what belief was running the show beneath the surface.
        </p>
        <p style={{ fontSize: '14px', color: V.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.9, marginBottom: '14px' }}>That is what this platform is for.</p>
        <p style={{ fontSize: '14px', color: V.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.9 }}>
          Set up both. Use both. They work together — ThinkorSwim shows you what happened. Trade Like a Woman shows you why.
        </p>
      </div>

      {/* Callout box */}
      <div style={{ background: V.bg3, border: `1px solid ${V.gold}`, borderRadius: '10px', padding: '16px 20px', marginBottom: '32px' }}>
        <p style={{ fontSize: '13px', color: V.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.8, margin: 0 }}>
          This is not a recommendation to open a brokerage account. You do not need real money to complete this program. ThinkorSwim has a paper trading (simulated) mode that is completely free and requires no funding. That is all you need to begin.
        </p>
      </div>

      {/* ── Complete Your Trading Profile ── */}
      {(() => {
        const ch0Whys = (state && state.whys) || [];
        const ch0Goals = (state && state.financialGoals) || [];
        const ch0Acct = (state && state.accountSize) || 0;
        const whyDone = ch0Whys.length > 0;
        const goalsDone = ch0Goals.length > 0;
        const acctDone = ch0Acct > 0;
        const allDone = whyDone && goalsDone && acctDone;
        return (
          <div style={{ marginBottom: '32px' }}>
            <h2 style={{ ...serif, fontSize: '22px', fontWeight: 400, color: V.gold, marginBottom: '6px' }}>Complete Your Trading Profile</h2>
            <p style={{ fontSize: '13px', color: V.muted, fontFamily: "'Jost',sans-serif", fontStyle: 'italic', marginBottom: '20px', lineHeight: 1.7 }}>Five minutes now. Better experience forever.</p>
            <div style={{ background: V.bg2, borderLeft: `3px solid ${V.gold}`, borderRadius: '10px', padding: '20px', marginBottom: '20px' }}>
              <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '22px', fontWeight: 400, color: V.gold, marginBottom: '10px', lineHeight: 1.3 }}>Five Minutes Now. Better Experience Forever.</h3>
              <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, lineHeight: 1.8, margin: 0, whiteSpace: 'pre-line' }}>{`Before you set up ThinkorSwim complete your trading profile.\n\nThe platform uses this information to personalize your experience and keep your most important intentions visible every day.\n\nIt takes five minutes. Do it now before you go further.`}</p>
            </div>
            {allDone ? (
              <div style={{ background: 'rgba(122,171,138,0.1)', border: `1px solid ${V.sage}`, borderRadius: '10px', padding: '20px', marginBottom: '20px', textAlign: 'center' }}>
                <div style={{ fontSize: '24px', color: V.sage, marginBottom: '10px' }}>♀</div>
                <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '22px', color: V.sage, marginBottom: '12px' }}>Your Profile Is Complete</div>
                <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, lineHeight: 1.8, margin: 0, whiteSpace: 'pre-line' }}>{`Your WHY is set.\nYour goals are written.\nYour account is configured.\nYour community is waiting.\n\nNow set up your platform.\nEverything below prepares you for your first real trade.`}</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
                <div style={{ background: V.bg3, border: `1px solid ${whyDone ? 'rgba(122,171,138,0.4)' : 'rgba(196,122,122,0.3)'}`, borderRadius: '8px', padding: '16px', display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                  <div style={{ fontSize: '16px', color: whyDone ? V.sage : V.rose, flexShrink: 0, marginTop: '2px' }}>{whyDone ? '✓' : '○'}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', fontWeight: 700, color: V.text, marginBottom: '4px' }}>{whyDone ? 'WHY I Trade — Complete ✓' : 'Write Your WHY'}</div>
                    <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.muted, lineHeight: 1.7, margin: whyDone ? 0 : '0 0 10px' }}>{whyDone ? 'Your WHY is set. You can update it anytime from your dashboard.' : 'Your reason for being here. Visible every morning on your dashboard.'}</p>
                    {!whyDone && <button style={{ ...btnGold, padding: '7px 14px', fontSize: '12px' }} onClick={() => onNavigate && onNavigate('dashboard')}>Write My WHY →</button>}
                  </div>
                </div>
                <div style={{ background: V.bg3, border: `1px solid ${goalsDone ? 'rgba(122,171,138,0.4)' : 'rgba(196,122,122,0.3)'}`, borderRadius: '8px', padding: '16px', display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                  <div style={{ fontSize: '16px', color: goalsDone ? V.sage : V.rose, flexShrink: 0, marginTop: '2px' }}>{goalsDone ? '✓' : '○'}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', fontWeight: 700, color: V.text, marginBottom: '4px' }}>{goalsDone ? 'My Goals — Complete ✓' : 'Set Your Goals'}</div>
                    <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.muted, lineHeight: 1.7, margin: goalsDone ? 0 : '0 0 10px' }}>{goalsDone ? 'Your goals are set. You can add more anytime from your dashboard.' : 'What you want to achieve and by when. Lives on your dashboard alongside your WHY.'}</p>
                    {!goalsDone && <button style={{ ...btnGold, padding: '7px 14px', fontSize: '12px' }} onClick={() => onNavigate && onNavigate('dashboard')}>Set My Goals →</button>}
                  </div>
                </div>
                <div style={{ background: V.bg3, border: `1px solid ${acctDone ? 'rgba(122,171,138,0.4)' : 'rgba(196,122,122,0.3)'}`, borderRadius: '8px', padding: '16px', display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                  <div style={{ fontSize: '16px', color: acctDone ? V.sage : V.rose, flexShrink: 0, marginTop: '2px' }}>{acctDone ? '✓' : '○'}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', fontWeight: 700, color: V.text, marginBottom: '4px' }}>{acctDone ? 'Account Size — Complete ✓' : 'Enter Your Account Size'}</div>
                    <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.muted, lineHeight: 1.7, margin: acctDone ? 0 : '0 0 10px' }}>{acctDone ? 'Your account size is set. Position sizing and risk calculations are active.' : 'Your trading capital. Used to calculate position sizing and risk limits throughout the platform.'}</p>
                    {!acctDone && <button style={{ ...btnGold, padding: '7px 14px', fontSize: '12px' }} onClick={() => onNavigate && onNavigate('config')}>Enter Account Size →</button>}
                  </div>
                </div>
                <div style={{ background: V.bg3, border: `1px solid rgba(201,168,76,0.2)`, borderRadius: '8px', padding: '16px', display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                  <div style={{ fontSize: '16px', color: V.gold, flexShrink: 0, marginTop: '2px' }}>○</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', fontWeight: 700, color: V.text, marginBottom: '4px' }}>Join the Community</div>
                    <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.muted, lineHeight: 1.7, margin: '0 0 10px' }}>Connect with other students going through this program.</p>
                    <a href="https://www.facebook.com/groups/1519037119571290" target="_blank" rel="noopener noreferrer" style={{ ...btnGold, padding: '7px 14px', fontSize: '12px', textDecoration: 'none', display: 'inline-block' }}>Join Now →</a>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Setup sections */}
      {sections.map(sec => (
        <div key={sec.step} style={{ background: V.bg2, border: `0.5px solid ${V.border}`, borderRadius: '12px', padding: '24px 28px', marginBottom: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
            <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'rgba(201,168,76,0.1)', border: `0.5px solid ${V.gold}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span style={{ ...mono, fontSize: '11px', color: V.gold }}>{sec.step}</span>
            </div>
            <h2 style={{ ...serif, fontSize: '20px', fontWeight: 400, color: V.gold, margin: 0 }}>Step {sec.step} — {sec.title}</h2>
          </div>
          {sec.topCard && (
            <div style={{ background: V.bg3, border: `0.5px solid rgba(201,168,76,0.2)`, borderRadius: '8px', padding: '16px 20px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <span style={{ fontSize: '16px' }}>{sec.topCard.icon}</span>
                <span style={{ fontSize: '13px', fontWeight: 700, color: V.gold, fontFamily: "'Jost',sans-serif", letterSpacing: '0.04em' }}>{sec.topCard.label}</span>
              </div>
              <p style={{ fontSize: '13px', color: V.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.8, margin: 0, whiteSpace: 'pre-line' }}>{sec.topCard.body}</p>
            </div>
          )}
          <div style={{ fontSize: '14px', color: V.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.9, whiteSpace: 'pre-line', marginBottom: sec.colorNote || sec.cards || sec.linkText ? '14px' : 0 }}>
            {sec.body}
          </div>
          {sec.cards && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '14px' }}>
              {sec.cards.map((c, ci) => (
                <div key={ci} style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: '10px', padding: '16px 20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    {c.icon
                      ? <span style={{ fontSize: '16px', flexShrink: 0 }}>{c.icon}</span>
                      : <div style={{ width: '11px', height: '11px', borderRadius: '50%', background: c.color, flexShrink: 0 }} />
                    }
                    <span style={{ fontSize: '13px', fontWeight: 700, color: c.color, fontFamily: "'Jost',sans-serif", letterSpacing: '0.04em' }}>{c.label}</span>
                  </div>
                  <p style={{ fontSize: '13px', color: 'rgba(240,237,230,0.8)', fontFamily: "'Jost',sans-serif", lineHeight: 1.75, margin: c.tos ? '0 0 10px' : 0, whiteSpace: 'pre-line' }}>{c.desc}</p>
                  {c.tos && (
                    <div style={{ background: 'rgba(0,0,0,0.25)', borderRadius: '6px', padding: '7px 12px' }}>
                      <span style={{ ...mono, fontSize: '11px', color: c.color }}>{c.tos}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {sec.showColumnsEditor && (
            <div style={{ marginBottom: '14px' }}>
              <TOSColumnsEditorMockup />
              {sec.columnsEditorNote && (
                <div style={{ background: V.bg2, borderLeft: `3px solid ${V.gold}`, borderRadius: '0 8px 8px 0', padding: '16px 20px', marginTop: '4px' }}>
                  <div style={{ fontSize: '13px', color: V.gold, fontFamily: "'Jost',sans-serif", fontWeight: 700, letterSpacing: '0.06em', marginBottom: '10px' }}>HOW TO USE THE COLUMNS EDITOR</div>
                  <p style={{ fontSize: '13px', color: V.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.8, margin: 0, whiteSpace: 'pre-line' }}>{sec.columnsEditorNote}</p>
                </div>
              )}
            </div>
          )}
          {sec.cardNote && (
            <div style={{ padding: '12px 16px', background: 'rgba(201,168,76,0.06)', borderLeft: `3px solid ${V.gold}`, borderRadius: '8px', marginBottom: sec.linkText ? '14px' : 0 }}>
              <p style={{ fontSize: '13px', color: V.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.75, margin: 0 }}>{sec.cardNote}</p>
            </div>
          )}
          {sec.positionsColumns && (
            <div style={{ background: V.bg3, border: `0.5px solid rgba(201,168,76,0.2)`, borderRadius: '8px', padding: '20px', marginBottom: '14px' }}>
              {sec.positionsColumns.map((col, ci) => (
                <div key={ci} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', padding: ci < sec.positionsColumns.length - 1 ? '0 0 12px' : '0', marginBottom: ci < sec.positionsColumns.length - 1 ? '12px' : 0, borderBottom: ci < sec.positionsColumns.length - 1 ? `0.5px solid rgba(240,237,230,0.06)` : 'none' }}>
                  <span style={{ ...mono, fontSize: '12px', color: V.gold, minWidth: '18px', marginTop: '2px' }}>{ci + 1}.</span>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: V.text, fontFamily: "'Jost',sans-serif", marginBottom: '2px' }}>{col.name}</div>
                    <div style={{ fontSize: '12px', color: V.muted, fontFamily: "'Jost',sans-serif", fontStyle: 'italic', lineHeight: 1.6 }}>{col.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {sec.positionsNote && (
            <div style={{ padding: '12px 16px', background: 'rgba(201,168,76,0.06)', borderLeft: `3px solid ${V.gold}`, borderRadius: '8px', marginBottom: sec.linkText ? '14px' : 0 }}>
              <p style={{ fontSize: '13px', color: V.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.75, margin: 0 }}>{sec.positionsNote}</p>
            </div>
          )}
          {sec.colorNote && (
            <div style={{ marginTop: '14px', padding: '10px 16px', background: 'rgba(201,168,76,0.04)', borderRadius: '8px', borderLeft: `2px solid ${V.gold}` }}>
              <p style={{ ...serif, fontSize: '15px', fontStyle: 'italic', color: V.gold, margin: 0, lineHeight: 1.6 }}>{sec.colorNote}</p>
            </div>
          )}
          {sec.linkText && (
            <button style={{ ...btnGold, padding: '9px 18px', fontSize: '12px', marginTop: '10px', minHeight: '44px' }} onClick={() => onNavigate && onNavigate(sec.linkView)}>
              {sec.linkText}
            </button>
          )}
        </div>
      ))}

      {/* Economic Calendar awareness */}
      <div style={{ background: V.bg2, borderLeft: `3px solid ${V.gold}`, borderRadius: '8px', padding: '14px', marginBottom: '20px' }}>
        <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.muted, fontStyle: 'italic', lineHeight: 1.8, margin: 0 }}>
          The Economic Calendar shows upcoming market events including earnings announcements and Triple Witching dates — the four most volatile days of the options calendar. Check it every morning alongside your Market Brief.
        </p>
      </div>

      {/* Checklist */}
      <div style={{ marginTop: '40px', marginBottom: '32px' }}>
        <h2 style={{ ...serif, fontSize: '22px', fontWeight: 400, color: V.gold, marginBottom: '6px' }}>Your Setup Checklist</h2>
        <p style={{ fontSize: '13px', color: V.muted, fontFamily: "'Jost',sans-serif", fontStyle: 'italic', marginBottom: '20px', lineHeight: 1.7 }}>
          Check each item as you complete it. Your progress is saved automatically.
        </p>
        <div style={{ background: V.bg2, border: `0.5px solid ${V.border}`, borderRadius: '12px', overflow: 'hidden', marginBottom: '16px' }}>
          {CHECKLIST_ITEMS.map((item, i) => (
            <div key={i} onClick={() => toggleItem(i)}
              style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 20px', borderBottom: i < CHECKLIST_ITEMS.length - 1 ? `0.5px solid ${V.border}` : 'none', cursor: 'pointer', background: checklist[i] ? 'rgba(122,171,138,0.04)' : 'transparent' }}>
              <div style={{ width: '20px', height: '20px', borderRadius: '4px', border: `1px solid ${checklist[i] ? V.sage : 'rgba(240,237,230,0.2)'}`, background: checklist[i] ? 'rgba(122,171,138,0.15)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '12px', color: V.sage }}>
                {checklist[i] ? '✓' : ''}
              </div>
              <span style={{ fontSize: '13px', color: checklist[i] ? V.text : V.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.5 }}>{item}</span>
            </div>
          ))}
        </div>

        {/* Progress bar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
          <span style={{ fontSize: '12px', color: V.muted, fontFamily: "'Jost',sans-serif" }}>{completedCount} of {CHECKLIST_ITEMS.length} items complete</span>
          <span style={{ fontSize: '12px', color: allComplete ? V.sage : V.gold, fontFamily: "'DM Mono',monospace" }}>{Math.round((completedCount / CHECKLIST_ITEMS.length) * 100)}%</span>
        </div>
        <div style={{ height: '6px', background: V.bg3, borderRadius: '3px', overflow: 'hidden' }}>
          <div style={{ height: '100%', background: allComplete ? V.sage : V.gold, borderRadius: '3px', width: `${(completedCount / CHECKLIST_ITEMS.length) * 100}%`, transition: 'width 0.3s ease' }} />
        </div>
      </div>

      {/* Completion card */}
      {allComplete && (
        <div style={{ background: 'rgba(122,171,138,0.15)', border: `1px solid ${V.sage}`, borderRadius: '12px', padding: '28px 32px', marginBottom: '24px' }}>
          <h3 style={{ ...serif, fontSize: '20px', fontWeight: 400, color: V.sage, marginBottom: '12px' }}>Your platform is ready.</h3>
          <p style={{ fontSize: '14px', color: V.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.8, marginBottom: '20px' }}>
            You have everything you need to begin. The simulators in this program are built to match your TOS setup exactly — the same indicators, the same colors, the same layout. When you see something in a simulator, you will know exactly where to find it on your real platform.
          </p>
          <button style={{ ...btnGold, padding: '12px 24px', fontSize: '13px', minHeight: '44px' }} onClick={() => onNavigate && onNavigate('chapter_ch01')}>
            Begin Chapter 1 →
          </button>
        </div>
      )}

      {/* TOS simulator card */}
      <div style={{ background: V.bg2, border: `0.5px solid ${V.gold}`, borderRadius: '12px', padding: '20px 24px', marginBottom: '20px' }}>
        <p style={{ fontSize: '14px', color: V.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.7, marginBottom: '14px' }}>
          Want to see what your finished setup looks like before trading real money?
        </p>
        <button style={{ ...btnGold, padding: '10px 20px', fontSize: '13px', minHeight: '44px' }} onClick={() => onNavigate && onNavigate('simulator')}>
          Open the Options Chain Simulator →
        </button>
      </div>
    </div>
  );
}

export default Ch0View
